/**
 * support.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Admin support management — session-gated, full sidebar, design-matched to
 * transactions.jsx / copy_trades.jsx.
 * Route: /admin/support
 *
 * Tables used:
 *   support_tickets  (id, user_id → users, subject, status, created_at)
 *   ticket_messages  (id, ticket_id, sender_id → users, body, created_at)
 *   users            (id, first_name, last_name, email)
 */

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import supabase from "../supabase";
import {
  // sidebar
  LayoutDashboard, Wallet, Receipt, Copy, UserCheck,
  HeadphonesIcon, Bell, CandlestickChart, BarChart2, ShoppingBag,
  ChevronRight, LogOut,CreditCard, MapPin,
  // page
  Search, RefreshCw, X, Send, CheckCircle2, AlertCircle,
  Clock, XCircle, ChevronUp, ChevronDown, MessageSquare,
  Shield, Users, Mail, Eye, Code2, Menu,
} from "lucide-react";

const PER_PAGE = 15;

// ─── Sidebar nav ─────────────────────────────────────────────────────────────
const NAV_SECTIONS = [
  {
    label: "Overview",
    items: [{ to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true }],
  },
  {
    label: "Users & Finance",
    items: [
      { to: "/admin/users",        label: "Users",        icon: Users    },
      { to: "/admin/wallets",      label: "Wallets",      icon: Wallet   },
      { to: "/admin/transactions", label: "Transactions", icon: Receipt  },
    ],
  },
  {
    label: "Trading",
    items: [
      { to: "/admin/copy-trades",  label: "Copy trades",   icon: Copy             },
      { to: "/admin/traders",      label: "Hired traders", icon: UserCheck        },
      { to: "/admin/trading",      label: "Open trades",   icon: CandlestickChart },
      { to: "/admin/stock-trades", label: "Stock trades",  icon: BarChart2        },
      { to: "/admin/marketplace",  label: "Marketplace",   icon: ShoppingBag      },
    ],
  },
  {
    label: "Communication",
    items: [
      { to: "/admin/support",       label: "Support",       icon: HeadphonesIcon },
      { to: "/admin/notifications", label: "Notifications", icon: Bell           },
    ],
  },
   {
    label: "Settings",
    items: [
      { to: "/admin/subscriptions",      label: "Subscriptions",      icon: CreditCard },
      { to: "/admin/payment-addresses",  label: "Payment Addresses",  icon: MapPin     },
      { to: "/admin/market-subscriptions",  label: "Market Subscriptions",  icon: MapPin     },
    ],
  },
];

// ─── Styles ───────────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg-base:      #06060f;
    --bg-surface:   #0a0a16;
    --bg-elevated:  #0e0e1c;
    --bg-card:      #111122;
    --bg-hover:     #14142a;
    --border:       rgba(255,255,255,0.055);
    --border-hi:    rgba(255,255,255,0.11);
    --accent:       #6366f1;
    --accent-light: #818cf8;
    --accent-dim:   rgba(99,102,241,0.13);
    --accent-glow:  rgba(99,102,241,0.22);
    --green:        #34d399;
    --green-dim:    rgba(52,211,153,0.13);
    --amber:        #fbbf24;
    --amber-dim:    rgba(251,191,36,0.13);
    --rose:         #fb7185;
    --rose-dim:     rgba(251,113,133,0.13);
    --cyan:         #22d3ee;
    --cyan-dim:     rgba(34,211,238,0.13);
    --purple:       #a78bfa;
    --text-primary:   #eeeeff;
    --text-secondary: #7878a0;
    --text-muted:     #3e3e58;
    --font-sans: 'Inter', system-ui, sans-serif;
    --font-mono: 'JetBrains Mono', monospace;
    --sidebar-w: 232px;
    --topbar-h:  60px;
    --radius-sm: 8px;
    --radius-md: 14px;
    --radius-lg: 18px;
  }
  html, body, #root { height: 100%; background: var(--bg-base); font-family: var(--font-sans); color-scheme: dark; }

  /* Shell */
  .sp-shell { display: flex; height: 100vh; overflow: hidden; background: var(--bg-base); position: relative; }
  .sp-shell::before { content: ''; position: fixed; inset: 0; pointer-events: none; z-index: 0; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E"); opacity: 0.018; }

  /* Sidebar */
  .ad-sidebar { width: var(--sidebar-w); flex-shrink: 0; height: 100%; min-height: 0; display: flex; flex-direction: column; background: var(--bg-surface); border-right: 1px solid var(--border); position: relative; z-index: 10; }
  .ad-sidebar::after { content: ''; position: absolute; top: 0; right: 0; bottom: 0; width: 1px; background: linear-gradient(to bottom, transparent 0%, var(--accent-glow) 35%, rgba(99,102,241,0.08) 70%, transparent 100%); pointer-events: none; }
  .ad-logo { display: flex; align-items: center; gap: 11px; padding: 0 18px; height: var(--topbar-h); border-bottom: 1px solid var(--border); flex-shrink: 0; }
  .ad-logo-icon { width: 32px; height: 32px; border-radius: 10px; background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a78bfa 100%); display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 0 0 1px rgba(99,102,241,0.4), 0 4px 16px rgba(99,102,241,0.35); }
  .ad-logo-text strong { font-size: 13.5px; font-weight: 800; letter-spacing: -0.4px; background: linear-gradient(to right, #eeeeff, #a5b4fc); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
  .ad-logo-text span { font-size: 9.5px; color: var(--text-muted); display: block; margin-top: 3px; letter-spacing: 0.8px; text-transform: uppercase; font-weight: 500; }
  .ad-nav { flex: 1; min-height: 0; overflow-y: auto; padding: 18px 10px; display: flex; flex-direction: column; gap: 22px; }
  .ad-nav::-webkit-scrollbar { width: 0; }
  .ad-nav-section-label { font-size: 9px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--text-muted); padding: 0 10px; margin-bottom: 5px; }
  .ad-nav-items { display: flex; flex-direction: column; gap: 1px; }
  .ad-nav-link { display: flex; align-items: center; gap: 9px; padding: 8px 10px; border-radius: var(--radius-sm); font-size: 12.5px; font-weight: 500; color: var(--text-secondary); text-decoration: none; transition: all 0.18s ease; position: relative; }
  .ad-nav-link:hover { background: var(--bg-hover); color: var(--text-primary); }
  .ad-nav-link.active { background: linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(139,92,246,0.10) 100%); color: var(--accent-light); font-weight: 600; box-shadow: inset 0 0 0 1px rgba(99,102,241,0.15); }
  .ad-nav-link.active::before { content: ''; position: absolute; left: 0; top: 18%; bottom: 18%; width: 2.5px; border-radius: 99px; background: linear-gradient(to bottom, var(--accent), var(--purple)); box-shadow: 0 0 10px var(--accent-glow); }
  .ad-nav-link svg { flex-shrink: 0; opacity: 0.65; transition: opacity 0.15s; }
  .ad-nav-link:hover svg, .ad-nav-link.active svg { opacity: 1; }
  .ad-nav-chevron { margin-left: auto; opacity: 0.35; }
  .ad-user-card { flex-shrink: 0; border-top: 1px solid var(--border); padding: 12px 10px; background: linear-gradient(to top, rgba(99,102,241,0.04) 0%, transparent 100%); }
  .ad-user-inner { display: flex; align-items: center; gap: 10px; padding: 9px 10px; border-radius: var(--radius-sm); transition: background 0.15s; }
  .ad-user-inner:hover { background: var(--bg-hover); }
  .ad-user-avatar { width: 32px; height: 32px; border-radius: 50%; background: linear-gradient(135deg, var(--accent-dim), rgba(139,92,246,0.2)); border: 1.5px solid rgba(99,102,241,0.35); display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; color: var(--accent-light); flex-shrink: 0; }
  .ad-user-info { flex: 1; min-width: 0; }
  .ad-user-info strong { font-size: 12px; font-weight: 600; color: var(--text-primary); display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .ad-user-info span { font-size: 10px; color: var(--text-muted); display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 1px; }
  .ad-logout-btn { width: 28px; height: 28px; border-radius: 7px; background: none; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; color: var(--text-muted); transition: all 0.15s; }
  .ad-logout-btn:hover { background: var(--rose-dim); color: var(--rose); }

  /* Page */
  .sp-page { flex: 1; display: flex; flex-direction: column; overflow: hidden; position: relative; z-index: 1; min-height: 0; min-width: 0; }

  /* Topbar */
  .sp-topbar { flex-shrink: 0; height: var(--topbar-h); background: rgba(10,10,22,0.85); backdrop-filter: blur(12px); border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; padding: 0 28px; position: relative; }
  .sp-topbar::after { content: ''; position: absolute; bottom: -1px; left: 0; right: 0; height: 1px; background: linear-gradient(to right, transparent 0%, var(--accent-glow) 50%, transparent 100%); }
  .sp-topbar-left { display: flex; align-items: center; gap: 14px; }
  .sp-topbar-badge { display: flex; align-items: center; gap: 6px; padding: 3px 10px; border-radius: 99px; background: var(--accent-dim); border: 1px solid rgba(99,102,241,0.2); font-size: 10px; font-weight: 700; color: var(--accent-light); letter-spacing: 0.5px; text-transform: uppercase; }
  .sp-topbar-title { font-size: 15px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.4px; }
  .sp-topbar-sub { font-size: 11px; color: var(--text-muted); margin-top: 1px; }
  .sp-topbar-right { display: flex; align-items: center; gap: 8px; }
  .sp-btn { display: flex; align-items: center; gap: 6px; padding: 6px 14px; border-radius: var(--radius-sm); font-size: 11.5px; font-weight: 500; cursor: pointer; transition: all 0.18s; font-family: var(--font-sans); border: 1px solid var(--border-hi); background: var(--bg-card); color: var(--text-secondary); }
  .sp-btn:hover { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }

  /* Main */
  .sp-main { flex: 1; overflow-y: auto; overflow-x: hidden; min-height: 0; padding: 26px 28px; display: flex; flex-direction: column; gap: 20px; }
  .sp-main::-webkit-scrollbar { width: 4px; }
  .sp-main::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.25); border-radius: 99px; }

  /* Stat grid */
  .sp-stat-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; }
  .sp-stat { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-md); padding: 18px 20px; display: flex; flex-direction: column; gap: 12px; position: relative; overflow: hidden; transition: border-color 0.22s, box-shadow 0.22s, transform 0.18s; }
  .sp-stat::before { content: ''; position: absolute; inset: 0; background: radial-gradient(ellipse at 110% -10%, var(--stat-glow, transparent) 0%, transparent 60%); pointer-events: none; }
  .sp-stat::after { content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background: linear-gradient(to right, transparent 20%, var(--stat-bar, transparent) 50%, transparent 80%); pointer-events: none; }
  .sp-stat:hover { border-color: rgba(255,255,255,0.1); box-shadow: 0 4px 24px rgba(0,0,0,0.4); transform: translateY(-1px); }
  .sp-stat-top { display: flex; align-items: flex-start; justify-content: space-between; }
  .sp-stat-label { font-size: 9.5px; font-weight: 700; letter-spacing: 1.1px; text-transform: uppercase; color: var(--text-muted); }
  .sp-stat-icon { width: 32px; height: 32px; border-radius: 9px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .sp-stat-value { font-size: 22px; font-weight: 700; color: var(--text-primary); letter-spacing: -1px; font-family: var(--font-mono); line-height: 1; }
  .sp-stat-sub { font-size: 10.5px; color: var(--text-muted); margin-top: 4px; }

  /* Panel */
  .sp-panel { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-md); overflow: hidden; }
  .sp-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 20px; border-bottom: 1px solid var(--border); flex-wrap: wrap; }
  .sp-toolbar-left { display: flex; align-items: center; gap: 10px; }
  .sp-toolbar-right { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .sp-panel-title { font-size: 13px; font-weight: 700; color: var(--text-primary); }
  .sp-count-pill { padding: 2px 8px; border-radius: 99px; border: 1px solid var(--border-hi); font-size: 10px; color: var(--text-muted); font-family: var(--font-mono); }

  /* Status filter tabs */
  .sp-filter-group { display: flex; gap: 2px; padding: 3px; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-sm); }
  .sp-filter-btn { padding: 4px 11px; border-radius: 5px; font-size: 11px; font-weight: 500; background: none; border: none; cursor: pointer; color: var(--text-muted); font-family: var(--font-sans); transition: all 0.15s; text-transform: capitalize; }
  .sp-filter-btn:hover { color: var(--text-secondary); }
  .sp-filter-btn.active { background: var(--accent-dim); color: var(--accent-light); }
  .sp-filter-btn.active.open     { background: var(--amber-dim); color: var(--amber); }
  .sp-filter-btn.active.resolved { background: var(--green-dim); color: var(--green); }
  .sp-filter-btn.active.closed   { background: rgba(120,120,160,0.1); color: var(--text-secondary); }

  /* Search */
  .sp-search-wrap { position: relative; }
  .sp-search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--text-muted); pointer-events: none; }
  .sp-search { padding: 6px 12px 6px 30px; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-sm); font-size: 12px; color: var(--text-primary); font-family: var(--font-sans); width: 210px; outline: none; transition: border-color 0.18s; }
  .sp-search::placeholder { color: var(--text-muted); }
  .sp-search:focus { border-color: rgba(99,102,241,0.4); box-shadow: 0 0 0 3px rgba(99,102,241,0.08); }

  /* Table */
  .sp-table-wrap { overflow-x: auto; }
  .sp-table { width: 100%; border-collapse: collapse; }
  .sp-table thead tr { border-bottom: 1px solid var(--border); }
  .sp-table th { padding: 10px 18px; text-align: left; font-size: 9.5px; font-weight: 700; letter-spacing: 1.1px; text-transform: uppercase; color: var(--text-muted); white-space: nowrap; cursor: pointer; user-select: none; transition: color 0.15s; }
  .sp-table th:hover { color: var(--text-secondary); }
  .sp-table th.no-sort { cursor: default; }
  .sp-table tbody tr { border-bottom: 1px solid rgba(255,255,255,0.035); transition: background 0.12s; cursor: pointer; }
  .sp-table tbody tr:last-child { border-bottom: none; }
  .sp-table tbody tr:hover { background: rgba(99,102,241,0.04); }
  .sp-table td { padding: 13px 18px; vertical-align: middle; font-size: 13px; }

  /* Avatar */
  .sp-avatar-cell { display: flex; align-items: center; gap: 10px; }
  .sp-avatar { width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; flex-shrink: 0; }
  .sp-avatar-indigo { background: rgba(99,102,241,0.18); color: #818cf8; border: 1px solid rgba(99,102,241,0.25); }
  .sp-avatar-green  { background: rgba(52,211,153,0.15);  color: #34d399; border: 1px solid rgba(52,211,153,0.2);  }
  .sp-avatar-amber  { background: rgba(251,191,36,0.15);  color: #fbbf24; border: 1px solid rgba(251,191,36,0.2);  }
  .sp-avatar-rose   { background: rgba(251,113,133,0.15); color: #fb7185; border: 1px solid rgba(251,113,133,0.2); }
  .sp-avatar-purple { background: rgba(167,139,250,0.15); color: #a78bfa; border: 1px solid rgba(167,139,250,0.2); }
  .sp-cell-name { font-size: 13px; font-weight: 600; color: var(--text-primary); }
  .sp-cell-sub  { font-size: 11px; color: var(--text-secondary); margin-top: 1px; }

  /* Status chip */
  .sp-chip { display: inline-flex; align-items: center; gap: 4px; padding: 3px 9px; border-radius: 99px; font-size: 10px; font-weight: 600; letter-spacing: 0.3px; }
  .sp-chip.open     { background: var(--amber-dim); color: var(--amber); border: 1px solid rgba(251,191,36,0.2); }
  .sp-chip.resolved { background: var(--green-dim); color: var(--green); border: 1px solid rgba(52,211,153,0.2); }
  .sp-chip.closed   { background: rgba(120,120,160,0.1); color: var(--text-secondary); border: 1px solid var(--border); }
  /* Priority chips */
  .sp-priority { display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 5px; font-size: 10px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; }
  .sp-priority.low    { background: rgba(120,120,160,0.1); color: var(--text-secondary); border: 1px solid var(--border); }
  .sp-priority.medium { background: var(--accent-dim); color: var(--accent-light); border: 1px solid rgba(99,102,241,0.2); }
  .sp-priority.high   { background: var(--amber-dim); color: var(--amber); border: 1px solid rgba(251,191,36,0.2); }
  .sp-priority.urgent { background: var(--rose-dim); color: var(--rose); border: 1px solid rgba(251,113,133,0.2); }
  .sp-category { display: inline-flex; padding: 2px 8px; border-radius: 5px; font-size: 10px; font-weight: 600; background: var(--bg-elevated); color: var(--text-secondary); border: 1px solid var(--border-hi); text-transform: capitalize; }

  /* Open btn */
  .sp-open-btn { display: flex; align-items: center; gap: 6px; padding: 5px 12px; border-radius: var(--radius-sm); border: 1px solid var(--border-hi); background: transparent; color: var(--text-secondary); font-size: 11.5px; font-weight: 500; cursor: pointer; transition: all 0.15s; font-family: var(--font-sans); }
  .sp-open-btn:hover { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }

  /* Skeleton */
  .sp-skel { height: 12px; border-radius: 4px; background: rgba(255,255,255,0.05); animation: sp-pulse 1.4s ease-in-out infinite; }
  @keyframes sp-pulse { 0%,100%{opacity:1}50%{opacity:0.4} }

  /* Empty */
  .sp-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding: 48px; color: var(--text-muted); }
  .sp-empty span { font-size: 12.5px; }

  /* Pagination */
  .sp-pagination { display: flex; align-items: center; justify-content: space-between; padding: 12px 20px; border-top: 1px solid var(--border); }
  .sp-pag-info { font-size: 11px; color: var(--text-muted); }
  .sp-pag-pages { display: flex; gap: 4px; }
  .sp-pag-btn { width: 28px; height: 28px; border-radius: 6px; font-size: 11px; display: flex; align-items: center; justify-content: center; border: 1px solid var(--border); background: transparent; cursor: pointer; color: var(--text-secondary); transition: all 0.15s; font-family: var(--font-mono); }
  .sp-pag-btn:hover { background: var(--bg-elevated); color: var(--text-primary); }
  .sp-pag-btn.active { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }

  /* ── Ticket Modal ────────────────────────────────────────────────── */
  .sp-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.7); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 20px; }
  .sp-modal { background: var(--bg-elevated); border: 1px solid var(--border-hi); border-radius: var(--radius-lg); width: 100%; max-width: 640px; display: flex; flex-direction: column; max-height: 88vh; box-shadow: 0 24px 80px rgba(0,0,0,0.65); }

  /* Modal header */
  .sp-modal-head { display: flex; align-items: flex-start; justify-content: space-between; padding: 20px 22px 18px; border-bottom: 1px solid var(--border); flex-shrink: 0; gap: 12px; }
  .sp-modal-head-meta { display: flex; align-items: flex-start; gap: 12px; flex: 1; min-width: 0; }
  .sp-modal-subject { font-size: 14px; font-weight: 700; color: var(--text-primary); line-height: 1.3; }
  .sp-modal-byline { font-size: 11px; color: var(--text-muted); margin-top: 3px; }
  .sp-modal-tags { display: flex; align-items: center; gap: 7px; margin-top: 8px; flex-wrap: wrap; }
  .sp-modal-date { font-size: 10px; color: var(--text-muted); }
  .sp-close-btn { width: 28px; height: 28px; border-radius: 7px; border: 1px solid var(--border); background: transparent; cursor: pointer; display: flex; align-items: center; justify-content: center; color: var(--text-secondary); transition: all 0.15s; flex-shrink: 0; }
  .sp-close-btn:hover { background: var(--bg-hover); color: var(--text-primary); }

  /* Messages */
  .sp-messages { flex: 1; overflow-y: auto; padding: 18px 22px; display: flex; flex-direction: column; gap: 14px; }
  .sp-messages::-webkit-scrollbar { width: 3px; }
  .sp-messages::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.2); border-radius: 99px; }
  .sp-msg-row { display: flex; gap: 10px; }
  .sp-msg-row.admin { flex-direction: row-reverse; }
  .sp-msg-bubble-wrap { display: flex; flex-direction: column; max-width: 70%; }
  .sp-msg-row.admin .sp-msg-bubble-wrap { align-items: flex-end; }
  .sp-msg-bubble { padding: 11px 14px; border-radius: var(--radius-md); font-size: 13px; line-height: 1.55; color: var(--text-primary); }
  .sp-msg-bubble.user  { background: rgba(255,255,255,0.055); border-radius: var(--radius-md) var(--radius-md) var(--radius-md) 4px; border: 1px solid var(--border); }
  .sp-msg-bubble.admin { background: linear-gradient(135deg, rgba(99,102,241,0.2) 0%, rgba(139,92,246,0.14) 100%); border-radius: var(--radius-md) var(--radius-md) 4px var(--radius-md); border: 1px solid rgba(99,102,241,0.18); }
  .sp-msg-meta { font-size: 10px; color: var(--text-muted); margin-top: 5px; padding: 0 2px; }
  .sp-msg-avatar { width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 700; flex-shrink: 0; margin-top: 2px; }
  .sp-msg-avatar.admin-av { background: var(--accent-dim); border: 1px solid rgba(99,102,241,0.3); color: var(--accent-light); }

  /* Reply box */
  .sp-reply { flex-shrink: 0; border-top: 1px solid var(--border); padding: 16px 22px; display: flex; flex-direction: column; gap: 12px; background: rgba(6,6,15,0.4); }
  .sp-reply-input-row { display: flex; gap: 10px; align-items: flex-end; }
  .sp-reply-textarea { flex: 1; padding: 10px 13px; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-sm); font-size: 13px; color: var(--text-primary); font-family: var(--font-sans); outline: none; resize: none; line-height: 1.5; transition: border-color 0.18s; }
  .sp-reply-textarea:focus { border-color: rgba(99,102,241,0.4); box-shadow: 0 0 0 3px rgba(99,102,241,0.08); }
  .sp-reply-textarea::placeholder { color: var(--text-muted); }
  .sp-reply-textarea:disabled { opacity: 0.4; cursor: not-allowed; }
  .sp-send-btn { display: flex; align-items: center; gap: 6px; padding: 9px 16px; border-radius: var(--radius-sm); border: none; background: var(--accent); color: #fff; font-size: 12.5px; font-weight: 600; cursor: pointer; font-family: var(--font-sans); transition: all 0.18s; flex-shrink: 0; }
  .sp-send-btn:hover:not(:disabled) { background: #4f52e0; box-shadow: 0 0 20px rgba(99,102,241,0.35); }
  .sp-send-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  /* Status action buttons */
  .sp-status-row { display: flex; align-items: center; justify-content: space-between; }
  .sp-status-actions { display: flex; gap: 6px; }
  .sp-action-btn { display: flex; align-items: center; gap: 5px; padding: 5px 11px; border-radius: var(--radius-sm); font-size: 11px; font-weight: 600; cursor: pointer; border: 1px solid; font-family: var(--font-sans); transition: all 0.15s; }
  .sp-action-btn.resolve { background: var(--green-dim); color: var(--green); border-color: rgba(52,211,153,0.2); }
  .sp-action-btn.resolve:hover { background: rgba(52,211,153,0.22); }
  .sp-action-btn.close   { background: rgba(120,120,160,0.1); color: var(--text-secondary); border-color: var(--border); }
  .sp-action-btn.close:hover { background: rgba(120,120,160,0.18); }
  .sp-action-btn.reopen  { background: var(--amber-dim); color: var(--amber); border-color: rgba(251,191,36,0.2); }
  .sp-action-btn.reopen:hover { background: rgba(251,191,36,0.22); }
  .sp-action-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .sp-msg-count { font-size: 11px; color: var(--text-muted); }
  .sp-closed-note { text-align: center; font-size: 12px; color: var(--text-muted); padding: 4px 0; }

  /* Toast */
  .sp-toast { position: fixed; bottom: 24px; right: 24px; z-index: 100; display: flex; align-items: center; gap: 10px; padding: 12px 18px; border-radius: var(--radius-md); background: var(--bg-elevated); border: 1px solid var(--border-hi); box-shadow: 0 8px 32px rgba(0,0,0,0.5); font-size: 13px; color: var(--text-primary); animation: sp-slide-in 0.2s ease; }
  .sp-toast.ok  { border-color: rgba(52,211,153,0.35); }
  .sp-toast.err { border-color: rgba(251,113,133,0.35); }
  @keyframes sp-slide-in { from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)} }

  /* Gate */
  .sp-gate { height: 100vh; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 14px; background: var(--bg-base); }

  /* ── Send Email Modal ─────────────────────────────────────────────── */
  .em-modal { background: var(--bg-elevated); border: 1px solid var(--border-hi); border-radius: var(--radius-lg); width: 100%; max-width: 580px; display: flex; flex-direction: column; max-height: 90vh; box-shadow: 0 24px 80px rgba(0,0,0,0.65); }
  .em-head { display: flex; align-items: center; justify-content: space-between; padding: 18px 22px; border-bottom: 1px solid var(--border); flex-shrink: 0; }
  .em-title { font-size: 14px; font-weight: 700; color: var(--text-primary); display: flex; align-items: center; gap: 8px; }
  .em-title-icon { width: 28px; height: 28px; border-radius: 8px; background: var(--accent-dim); border: 1px solid rgba(99,102,241,0.25); display: flex; align-items: center; justify-content: center; }
  .em-body { flex: 1; overflow-y: auto; padding: 22px; display: flex; flex-direction: column; gap: 16px; }
  .em-body::-webkit-scrollbar { width: 3px; }
  .em-body::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.2); border-radius: 99px; }
  .em-field { display: flex; flex-direction: column; gap: 6px; }
  .em-label { font-size: 10.5px; font-weight: 700; letter-spacing: 0.8px; text-transform: uppercase; color: var(--text-muted); }
  .em-select { padding: 9px 12px; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-sm); font-size: 13px; color: var(--text-primary); font-family: var(--font-sans); outline: none; appearance: none; cursor: pointer; transition: border-color 0.18s; width: 100%; }
  .em-select:focus { border-color: rgba(99,102,241,0.4); box-shadow: 0 0 0 3px rgba(99,102,241,0.08); }
  .em-input { padding: 9px 12px; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-sm); font-size: 13px; color: var(--text-primary); font-family: var(--font-sans); outline: none; transition: border-color 0.18s; width: 100%; }
  .em-input:focus { border-color: rgba(99,102,241,0.4); box-shadow: 0 0 0 3px rgba(99,102,241,0.08); }
  .em-input::placeholder { color: var(--text-muted); }
  .em-textarea { padding: 10px 12px; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-sm); font-size: 13px; color: var(--text-primary); font-family: var(--font-sans); outline: none; resize: vertical; line-height: 1.6; transition: border-color 0.18s; width: 100%; min-height: 160px; }
  .em-textarea:focus { border-color: rgba(99,102,241,0.4); box-shadow: 0 0 0 3px rgba(99,102,241,0.08); }
  .em-textarea::placeholder { color: var(--text-muted); }
  .em-preview-toggle { display: flex; gap: 4px; padding: 3px; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-sm); width: fit-content; }
  .em-tab { padding: 4px 12px; border-radius: 5px; font-size: 11px; font-weight: 500; background: none; border: none; cursor: pointer; color: var(--text-muted); font-family: var(--font-sans); transition: all 0.15s; }
  .em-tab.active { background: var(--accent-dim); color: var(--accent-light); }
  .em-preview-frame { background: #fff; border-radius: var(--radius-sm); border: 1px solid var(--border-hi); overflow: hidden; min-height: 160px; }
  .em-preview-frame iframe { width: 100%; border: none; display: block; }
  .em-user-info { display: flex; align-items: center; gap: 10px; padding: 10px 12px; background: var(--accent-dim); border: 1px solid rgba(99,102,241,0.18); border-radius: var(--radius-sm); }
  .em-user-info-text { font-size: 12px; color: var(--text-secondary); }
  .em-user-info-text strong { color: var(--text-primary); display: block; font-size: 13px; margin-bottom: 1px; }
  .em-footer { flex-shrink: 0; padding: 16px 22px; border-top: 1px solid var(--border); display: flex; justify-content: flex-end; gap: 8px; background: rgba(6,6,15,0.4); }
  .em-cancel-btn { display: flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: var(--radius-sm); border: 1px solid var(--border-hi); background: transparent; color: var(--text-secondary); font-size: 12.5px; font-weight: 500; cursor: pointer; font-family: var(--font-sans); transition: all 0.15s; }
  .em-cancel-btn:hover { background: var(--bg-hover); color: var(--text-primary); }
  .em-send-btn { display: flex; align-items: center; gap: 6px; padding: 8px 18px; border-radius: var(--radius-sm); border: none; background: var(--accent); color: #fff; font-size: 12.5px; font-weight: 600; cursor: pointer; font-family: var(--font-sans); transition: all 0.18s; }
  .em-send-btn:hover:not(:disabled) { background: #4f52e0; box-shadow: 0 0 20px rgba(99,102,241,0.35); }
  .em-send-btn:disabled { opacity: 0.45; cursor: not-allowed; }
  .em-select-wrap { position: relative; }
  .em-select-wrap::after { content: '▾'; position: absolute; right: 12px; top: 50%; transform: translateY(-50%); color: var(--text-muted); pointer-events: none; font-size: 11px; }

  /* ── Mobile nav controls ──────────────────────────────────────────── */
  .ad-hamburger { display: none; width: 32px; height: 32px; border-radius: 8px; border: 1px solid var(--border-hi); background: var(--bg-card); color: var(--text-secondary); align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; }
  .ad-hamburger:hover { color: var(--text-primary); }
  .ad-nav-close { display: none; margin-left: auto; width: 28px; height: 28px; border-radius: 7px; border: none; background: transparent; color: var(--text-secondary); align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; }
  .ad-backdrop { display: none; }

  /* ── Responsive breakpoints ───────────────────────────────────────── */
  @media (max-width: 1024px) {
    .ad-sidebar { position: fixed; top: 0; left: 0; bottom: 0; transform: translateX(-100%); transition: transform 0.22s ease; z-index: 60; box-shadow: 0 0 40px rgba(0,0,0,0.5); }
    .ad-sidebar.open { transform: translateX(0); }
    .ad-hamburger { display: flex; }
    .ad-nav-close { display: flex; }
    .ad-backdrop { display: block; position: fixed; inset: 0; background: rgba(0,0,0,0.55); backdrop-filter: blur(2px); z-index: 55; }
  }

  @media (max-width: 860px) {
    .sp-main { padding: 18px 16px; gap: 16px; }
    .sp-topbar { padding: 0 14px; flex-wrap: wrap; height: auto; min-height: var(--topbar-h); gap: 8px 0; padding-top: 10px; padding-bottom: 10px; }
    .sp-topbar-sub { display: none; }
    .sp-topbar-right { flex-wrap: wrap; }
    .sp-btn span { display: none; }
    .sp-toolbar { flex-direction: column; align-items: stretch; }
    .sp-toolbar-right { width: 100%; justify-content: space-between; }
    .sp-search-wrap { flex: 1; }
    .sp-search { width: 100%; }
    .sp-table th, .sp-table td { padding: 10px 12px; }
    .sp-table { min-width: 720px; }
    .sp-modal, .em-modal { max-width: 100%; max-height: 96vh; }
    .sp-overlay { padding: 0; align-items: flex-end; }
    .sp-modal, .em-modal { border-radius: var(--radius-lg) var(--radius-lg) 0 0; }
  }

  @media (max-width: 560px) {
    .sp-stat { padding: 14px 14px; gap: 8px; }
    .sp-stat-value { font-size: 18px; }
    .sp-topbar-title { font-size: 13.5px; }
    .sp-filter-group { width: 100%; justify-content: space-between; }
    .sp-filter-btn { flex: 1; text-align: center; padding: 4px 6px; }
    .sp-msg-bubble-wrap { max-width: 86%; }
    .sp-reply-input-row { flex-direction: column; align-items: stretch; }
    .sp-send-btn { justify-content: center; }
    .sp-status-row { flex-direction: column; align-items: stretch; gap: 10px; }
    .sp-status-actions { justify-content: flex-end; }
    .sp-pagination { flex-direction: column; gap: 8px; align-items: flex-start; }
  }
`;

function StyleInjector() {
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = STYLES;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);
  return null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtDate(str)     { if (!str) return "—"; return new Date(str).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
function fmtDateTime(str) { if (!str) return "—"; return new Date(str).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
function initials(first, last) { return `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase() || "?"; }
const PALETTE = ["indigo", "green", "amber", "rose", "purple"];
function avatarColor(id) {
  const n = typeof id === "string" ? id.charCodeAt(0) + (id.charCodeAt(id.length - 1) || 0) : (id ?? 0);
  return PALETTE[n % PALETTE.length];
}

// ─── Sidebar link ─────────────────────────────────────────────────────────────
function SidebarLink({ to, label, icon: Icon, end, onClick }) {
  return (
    <NavLink to={to} end={end} onClick={onClick} className={({ isActive }) => `ad-nav-link${isActive ? " active" : ""}`}>
      {({ isActive }) => (
        <>
          <Icon size={14} />
          <span style={{ flex: 1 }}>{label}</span>
          {isActive && <ChevronRight size={10} className="ad-nav-chevron" />}
        </>
      )}
    </NavLink>
  );
}

// ─── Status chip ──────────────────────────────────────────────────────────────
function StatusChip({ status }) {
  const icons = { open: Clock, resolved: CheckCircle2, closed: XCircle };
  const Icon = icons[status] ?? Clock;
  return (
    <span className={`sp-chip ${status ?? "open"}`}>
      <Icon size={9} />{status ?? "—"}
    </span>
  );
}

// ─── Sort icon ────────────────────────────────────────────────────────────────
function SortIcon({ field, sortField, sortDir }) {
  if (field !== sortField) return <ChevronUp size={9} style={{ opacity: 0.2, marginLeft: 4, display: "inline" }} />;
  return sortDir === 1
    ? <ChevronUp   size={9} style={{ color: "var(--accent-light)", marginLeft: 4, display: "inline" }} />
    : <ChevronDown size={9} style={{ color: "var(--accent-light)", marginLeft: 4, display: "inline" }} />;
}

// ─── Ticket modal ─────────────────────────────────────────────────────────────
function TicketModal({ ticket, adminId, onClose, onStatusChange, showToast }) {
  const [messages, setMessages] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [body,     setBody]     = useState("");
  const [sending,  setSending]  = useState(false);
  const [updating, setUpdating] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { fetchMessages(); }, [ticket.id]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  async function fetchMessages() {
    setLoading(true);
    const { data } = await supabase
      .from("ticket_messages")
      .select("id, body, created_at, sender_id, users(first_name, last_name)")
      .eq("ticket_id", ticket.id)
      .order("created_at", { ascending: true });
    setMessages(data ?? []);
    setLoading(false);
  }

  async function handleSend() {
    if (!body.trim()) return;
    setSending(true);
    try {
      const { error } = await supabase.from("ticket_messages").insert({
        ticket_id: ticket.id, sender_id: adminId,
        body: body.trim(), created_at: new Date().toISOString(),
      });
      if (error) throw error;
      setBody("");
      await fetchMessages();
    } catch (e) {
      showToast(e.message ?? "Failed to send.", false);
    } finally {
      setSending(false);
    }
  }

  async function handleStatus(newStatus) {
    setUpdating(true);
    try {
      const { error } = await supabase.from("support_tickets").update({ status: newStatus }).eq("id", ticket.id);
      if (error) throw error;
      showToast(`Ticket marked as ${newStatus}.`);
      onStatusChange(ticket.id, newStatus);
    } catch (e) {
      showToast(e.message ?? "Failed to update.", false);
    } finally {
      setUpdating(false);
    }
  }

  const user   = ticket.users;
  const aColor = avatarColor(ticket.id);
  const isClosed = ticket.status === "closed";

  return (
    <div className="sp-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sp-modal">

        {/* Head */}
        <div className="sp-modal-head">
          <div className="sp-modal-head-meta">
            <div className={`sp-avatar sp-avatar-${aColor}`} style={{ width: 36, height: 36, marginTop: 2, flexShrink: 0 }}>
              {initials(user?.first_name, user?.last_name)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="sp-modal-subject">{ticket.subject ?? "No subject"}</div>
              <div className="sp-modal-byline">{user?.first_name} {user?.last_name} · {user?.email}</div>
              <div className="sp-modal-tags">
                <StatusChip status={ticket.status} />
                {ticket.priority && <span className={`sp-priority ${ticket.priority}`}>{ticket.priority}</span>}
                {ticket.category && <span className="sp-category">{ticket.category}</span>}
                {ticket.ticket_ref && <span style={{ fontSize: 10, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>#{ticket.ticket_ref}</span>}
                <span className="sp-modal-date">Opened {fmtDate(ticket.created_at)}</span>
              </div>
            </div>
          </div>
          <button className="sp-close-btn" onClick={onClose}><X size={13} /></button>
        </div>

        {/* Messages */}
        <div className="sp-messages">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} style={{ display: "flex", justifyContent: i % 2 === 0 ? "flex-start" : "flex-end" }}>
                <div className="sp-skel" style={{ width: 220, height: 56, borderRadius: 12 }} />
              </div>
            ))
          ) : messages.length === 0 ? (
            <div className="sp-empty"><MessageSquare size={20} /><span>No messages yet in this ticket.</span></div>
          ) : messages.map((m) => {
            const isAdmin = m.sender_id === adminId;
            return (
              <div key={m.id} className={`sp-msg-row${isAdmin ? " admin" : ""}`}>
                <div className={`sp-msg-avatar${isAdmin ? " admin-av" : ` sp-avatar-${aColor}`}`}
                  style={!isAdmin ? { background: `var(--${aColor === "indigo" ? "accent" : aColor}-dim)`, color: `var(--${aColor === "indigo" ? "accent-light" : aColor})`, border: `1px solid rgba(var(--${aColor}-rgb, 99,102,241),0.25)` } : {}}>
                  {isAdmin ? <Shield size={11} /> : initials(m.users?.first_name, m.users?.last_name)}
                </div>
                <div className="sp-msg-bubble-wrap">
                  <div className={`sp-msg-bubble ${isAdmin ? "admin" : "user"}`}>{m.body}</div>
                  <div className="sp-msg-meta">{isAdmin ? "Admin" : (m.users?.first_name ?? "User")} · {fmtDateTime(m.created_at)}</div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {/* Reply box */}
        <div className="sp-reply">
          {isClosed ? (
            <p className="sp-closed-note">This ticket is closed — reopen it to reply.</p>
          ) : (
            <div className="sp-reply-input-row">
              <textarea
                className="sp-reply-textarea"
                rows={2}
                placeholder="Type your reply… (Enter to send, Shift+Enter for newline)"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                disabled={sending}
              />
              <button className="sp-send-btn" onClick={handleSend} disabled={sending || !body.trim()}>
                <Send size={13} />{sending ? "Sending…" : "Send"}
              </button>
            </div>
          )}
          <div className="sp-status-row">
            <div className="sp-status-actions">
              {ticket.status !== "resolved" && (
                <button className="sp-action-btn resolve" onClick={() => handleStatus("resolved")} disabled={updating}>
                  <CheckCircle2 size={11} />Mark resolved
                </button>
              )}
              {!isClosed ? (
                <button className="sp-action-btn close" onClick={() => handleStatus("closed")} disabled={updating}>
                  <XCircle size={11} />Close ticket
                </button>
              ) : (
                <button className="sp-action-btn reopen" onClick={() => handleStatus("open")} disabled={updating}>
                  <Clock size={11} />Reopen
                </button>
              )}
            </div>
            <span className="sp-msg-count">{messages.length} message{messages.length !== 1 ? "s" : ""}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Send Email Modal ─────────────────────────────────────────────────────────
function buildHtmlEmail({ toName, subject, message, adminEmail }) {
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1.0"/>
<title>${subject}</title>
</head>
<body style="margin:0;padding:0;background:#f4f4f8;font-family:'Segoe UI',Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f8;padding:32px 0;">
    <tr><td align="center">
      <table width="560" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.08);">
        <!-- Header -->
        <tr>
          <td style="background:linear-gradient(135deg,#6366f1 0%,#8b5cf6 50%,#a78bfa 100%);padding:28px 36px;text-align:center;">
            <div style="display:inline-block;background:rgba(255,255,255,0.15);border-radius:10px;padding:10px 18px;">
              <span style="font-size:20px;font-weight:800;color:#ffffff;letter-spacing:-0.5px;">Tradexflow</span>
            </div>
            <div style="margin-top:6px;font-size:11px;color:rgba(255,255,255,0.65);letter-spacing:1.5px;text-transform:uppercase;">Admin Message</div>
          </td>
        </tr>
        <!-- Body -->
        <tr>
          <td style="padding:36px 36px 28px;">
            <p style="margin:0 0 20px;font-size:15px;color:#1a1a2e;font-weight:600;">Hi ${toName},</p>
            <div style="font-size:14px;line-height:1.75;color:#3a3a5c;white-space:pre-wrap;">${message.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</div>
          </td>
        </tr>
        <!-- Divider -->
        <tr><td style="padding:0 36px;"><hr style="border:none;border-top:1px solid #ebebf5;margin:0;"/></td></tr>
        <!-- Footer -->
        <tr>
          <td style="padding:22px 36px 30px;text-align:center;">
            <p style="margin:0 0 4px;font-size:12px;color:#9090b0;">This message was sent by the Tradexflow support team.</p>
            <p style="margin:0;font-size:11px;color:#b0b0cc;">${adminEmail}</p>
          </td>
        </tr>
        <!-- Bottom bar -->
        <tr>
          <td style="background:linear-gradient(135deg,#6366f1,#8b5cf6);padding:10px 36px;text-align:center;">
            <span style="font-size:10px;color:rgba(255,255,255,0.55);letter-spacing:0.5px;">© ${new Date().getFullYear()} Tradexflow · All rights reserved</span>
          </td>
        </tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
}

function SendEmailModal({ users, adminEmail, onClose, showToast }) {
  const [recipientMode,  setRecipientMode]  = useState("single"); // "single" | "all"
  const [selectedUserId, setSelectedUserId] = useState("");
  const [subject,        setSubject]        = useState("");
  const [message,        setMessage]        = useState("");
  const [tab,            setTab]            = useState("compose"); // "compose" | "preview"
  const [sending,        setSending]        = useState(false);
  const iframeRef = useRef(null);

  const isBroadcast    = recipientMode === "all";
  const selectedUser   = users.find((u) => u.id === selectedUserId) ?? null;
  const toName         = isBroadcast ? "there" : (selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}` : "");
  const hasPreviewable = isBroadcast || !!selectedUser;
  const htmlPreview = hasPreviewable
    ? buildHtmlEmail({ toName, subject: subject || "(no subject)", message: message || "(no message)", adminEmail })
    : "";

  // Update iframe when preview tab is active
  useEffect(() => {
    if (tab === "preview" && iframeRef.current && htmlPreview) {
      const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
      if (doc) {
        doc.open();
        doc.write(htmlPreview);
        doc.close();
        // Auto-height
        setTimeout(() => {
          if (iframeRef.current) {
            iframeRef.current.style.height = (doc.body?.scrollHeight ?? 400) + "px";
          }
        }, 80);
      }
    }
  }, [tab, htmlPreview]);

  async function handleSend() {
    if (!subject.trim() || !message.trim()) return;
    if (!isBroadcast && !selectedUser) return;
    if (isBroadcast && users.length === 0) return;

    setSending(true);
    try {
      // Call Supabase Edge Function: supabase/functions/send-email/index.ts (uses Resend)
      const { data: { session } } = await supabase.auth.getSession();
      const endpoint = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`;
      const headers = {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${session?.access_token}`,
      };

      if (isBroadcast) {
        const html = buildHtmlEmail({ toName: "there", subject, message, adminEmail });
        const recipients = users.map((u) => ({ email: u.email, name: `${u.first_name} ${u.last_name}` }));
        const res = await fetch(endpoint, {
          method: "POST",
          headers,
          body: JSON.stringify({ broadcast: true, recipients, subject, html }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error ?? `HTTP ${res.status}`);
        }
        const result = await res.json().catch(() => ({}));
        showToast(`Email broadcast to ${result.sent ?? recipients.length} users`);
      } else {
        const html = buildHtmlEmail({ toName, subject, message, adminEmail });
        const res = await fetch(endpoint, {
          method: "POST",
          headers,
          body: JSON.stringify({ to: selectedUser.email, toName, subject, html }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error ?? `HTTP ${res.status}`);
        }
        showToast(`Email sent to ${selectedUser.email}`);
      }
      onClose();
    } catch (e) {
      showToast(e.message ?? "Failed to send email.", false);
    } finally {
      setSending(false);
    }
  }

  const canSend = subject.trim() && message.trim() && !sending
    && (isBroadcast ? users.length > 0 : !!selectedUser);

  return (
    <div className="sp-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="em-modal">

        {/* Head */}
        <div className="em-head">
          <div className="em-title">
            <div className="em-title-icon"><Mail size={13} color="var(--accent-light)" /></div>
            {isBroadcast ? "Broadcast Email to All Users" : "Send Email to User"}
          </div>
          <button className="sp-close-btn" onClick={onClose}><X size={13} /></button>
        </div>

        {/* Body */}
        <div className="em-body">

          {/* Recipient mode toggle */}
          <div className="em-field">
            <label className="em-label">Recipients</label>
            <div className="em-preview-toggle" style={{ marginBottom: 10 }}>
              <button
                className={`em-tab${recipientMode === "single" ? " active" : ""}`}
                onClick={() => setRecipientMode("single")}
              >
                <Mail size={10} style={{ display: "inline", marginRight: 4 }} />Single user
              </button>
              <button
                className={`em-tab${recipientMode === "all" ? " active" : ""}`}
                onClick={() => setRecipientMode("all")}
              >
                <Users size={10} style={{ display: "inline", marginRight: 4 }} />All users
              </button>
            </div>

            {recipientMode === "single" ? (
              <div className="em-select-wrap">
                <select
                  className="em-select"
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                >
                  <option value="">Choose a user…</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.first_name} {u.last_name} — {u.email}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="em-user-info">
                <div className="sp-avatar sp-avatar-purple"><Users size={14} /></div>
                <div className="em-user-info-text">
                  <strong>Broadcasting to all active users</strong>
                  {users.length} recipient{users.length === 1 ? "" : "s"} will receive this email individually
                </div>
              </div>
            )}
          </div>

          {/* User info preview */}
          {recipientMode === "single" && selectedUser && (
            <div className="em-user-info">
              <div className={`sp-avatar sp-avatar-${avatarColor(selectedUser.id)}`}>
                {initials(selectedUser.first_name, selectedUser.last_name)}
              </div>
              <div className="em-user-info-text">
                <strong>{selectedUser.first_name} {selectedUser.last_name}</strong>
                {selectedUser.email}
                {selectedUser.plan && <span style={{ marginLeft: 8, fontSize: 10, opacity: 0.6 }}>· {selectedUser.plan}</span>}
              </div>
            </div>
          )}

          {/* Subject */}
          <div className="em-field">
            <label className="em-label">Subject</label>
            <input
              className="em-input"
              type="text"
              placeholder="Email subject…"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          {/* Message + preview tabs */}
          <div className="em-field">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <label className="em-label" style={{ margin: 0 }}>Message</label>
              <div className="em-preview-toggle">
                <button className={`em-tab${tab === "compose" ? " active" : ""}`} onClick={() => setTab("compose")}>
                  <Code2 size={10} style={{ display: "inline", marginRight: 4 }} />Compose
                </button>
                <button
                  className={`em-tab${tab === "preview" ? " active" : ""}`}
                  onClick={() => setTab("preview")}
                  disabled={!hasPreviewable}
                  style={{ opacity: hasPreviewable ? 1 : 0.4 }}
                >
                  <Eye size={10} style={{ display: "inline", marginRight: 4 }} />Preview
                </button>
              </div>
            </div>

            {tab === "compose" ? (
              <textarea
                className="em-textarea"
                placeholder="Write your message here…&#10;&#10;This will be formatted inside a branded HTML email template and delivered to the user's inbox."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            ) : (
              <div className="em-preview-frame">
                <iframe ref={iframeRef} title="email-preview" style={{ width: "100%", border: "none", display: "block", minHeight: 300 }} />
              </div>
            )}
          </div>

        </div>

        {/* Footer */}
        <div className="em-footer">
          <button className="em-cancel-btn" onClick={onClose}><X size={12} />Cancel</button>
          <button className="em-send-btn" onClick={handleSend} disabled={!canSend}>
            <Mail size={12} />{sending ? (isBroadcast ? "Broadcasting…" : "Sending…") : (isBroadcast ? `Send to ${users.length} users` : "Send Email")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function AdminSupport() {
  const navigate = useNavigate();

  const [adminEmail,    setAdminEmail]    = useState("");
  const [adminInitials, setAdminInitials] = useState("A");
  const [adminId,       setAdminId]       = useState(null);
  const [sessionReady,  setSessionReady]  = useState(false);

  const [tickets,      setTickets]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField,    setSortField]    = useState("created_at");
  const [sortDir,      setSortDir]      = useState(-1);
  const [page,         setPage]         = useState(1);
  const [viewTicket,   setViewTicket]   = useState(null);
  const [toast,        setToast]        = useState({ msg: "", ok: true });
  const [emailModal,   setEmailModal]   = useState(false);
  const [allUsers,     setAllUsers]     = useState([]);
  const [navOpen,      setNavOpen]      = useState(false);

  // ── Session ───────────────────────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) { navigate("/admin/login", { replace: true }); return; }
      const email = session.user?.email ?? "";
      setAdminEmail(email);
      setAdminId(session.user.id);
      const parts = email.split("@")[0].split(".");
      setAdminInitials((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? parts[0]?.[1] ?? ""));
      setSessionReady(true);
    });
  }, [navigate]);

  async function handleLogout() {
    await supabase.auth.signOut();
    navigate("/admin/login", { replace: true });
  }

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchTickets = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("support_tickets")
      .select("id, ticket_ref, subject, category, priority, status, created_at, updated_at, resolved_at, users(id, first_name, last_name, email)")
      .order("created_at", { ascending: false });
    setTickets(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { if (sessionReady) fetchTickets(); }, [sessionReady, fetchTickets]);

  // ── Fetch all users for email dropdown ────────────────────────────────────
  useEffect(() => {
    if (!sessionReady) return;
    supabase
      .from("users")
      .select("id, first_name, last_name, email, plan, status")
      .eq("status", "active")
      .order("first_name", { ascending: true })
      .then(({ data }) => setAllUsers(data ?? []));
  }, [sessionReady]);

  function showToast(msg, ok = true) {
    setToast({ msg, ok });
    setTimeout(() => setToast({ msg: "", ok: true }), 3200);
  }

  function handleStatusChange(ticketId, newStatus) {
    setTickets((prev) => prev.map((t) => t.id === ticketId ? { ...t, status: newStatus } : t));
    if (viewTicket?.id === ticketId) setViewTicket((prev) => ({ ...prev, status: newStatus }));
  }

  // ── Filter + sort ─────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let list = tickets.filter((t) => {
      const matchSearch = !q
        || t.subject?.toLowerCase().includes(q)
        || `${t.users?.first_name ?? ""} ${t.users?.last_name ?? ""}`.toLowerCase().includes(q)
        || t.users?.email?.toLowerCase().includes(q);
      const matchStatus = statusFilter === "all" || t.status === statusFilter;
      return matchSearch && matchStatus;
    });
    list.sort((a, b) => {
      if (sortField === "user")    return `${a.users?.first_name}${a.users?.last_name}`.localeCompare(`${b.users?.first_name}${b.users?.last_name}`) * sortDir;
      if (sortField === "subject")  return (a.subject ?? "").localeCompare(b.subject ?? "") * sortDir;
      if (sortField === "category") return (a.category ?? "").localeCompare(b.category ?? "") * sortDir;
      if (sortField === "priority") { const ORDER = {urgent:0,high:1,medium:2,low:3}; return ((ORDER[a.priority]??4)-(ORDER[b.priority]??4))*sortDir; }
      return (new Date(a.created_at ?? 0) - new Date(b.created_at ?? 0)) * sortDir;
    });
    return list;
  }, [tickets, search, statusFilter, sortField, sortDir]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const pageSlice  = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  function handleSort(field) {
    if (field === sortField) setSortDir((d) => d * -1);
    else { setSortField(field); setSortDir(-1); }
    setPage(1);
  }

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total:    tickets.length,
    open:     tickets.filter((t) => t.status === "open").length,
    resolved: tickets.filter((t) => t.status === "resolved").length,
    closed:   tickets.filter((t) => t.status === "closed").length,
  }), [tickets]);

  const STAT_CARDS = [
    { label: "Total tickets", value: stats.total,    icon: HeadphonesIcon, bg: "rgba(99,102,241,0.15)",  color: "var(--accent-light)", glow: "rgba(99,102,241,0.08)",  bar: "var(--accent)", sub: "All time" },
    { label: "Open",          value: stats.open,     icon: Clock,          bg: "rgba(251,191,36,0.15)",  color: "var(--amber)",        glow: "rgba(251,191,36,0.08)",  bar: "var(--amber)",  sub: "Needs attention" },
    { label: "Resolved",      value: stats.resolved, icon: CheckCircle2,   bg: "rgba(52,211,153,0.15)",  color: "var(--green)",        glow: "rgba(52,211,153,0.08)",  bar: "var(--green)",  sub: "Successfully closed" },
    { label: "Closed",        value: stats.closed,   icon: XCircle,        bg: "rgba(120,120,160,0.13)", color: "var(--text-secondary)",glow: "rgba(120,120,160,0.06)", bar: "var(--text-secondary)", sub: "Archived" },
  ];

  const STATUS_FILTERS = ["all", "open", "resolved", "closed"];
  const COLS = [{ key: "user", label: "User" }, { key: "subject", label: "Subject" }, { key: "category", label: "Category" }, { key: "priority", label: "Priority" }, { key: "created_at", label: "Opened" }];

  if (!sessionReady) {
    return (
      <>
        <StyleInjector />
        <div className="sp-gate">
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#6366f1,#a78bfa)", boxShadow: "0 0 24px rgba(99,102,241,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <HeadphonesIcon size={18} color="#fff" />
          </div>
          <span style={{ fontSize: 13, color: "var(--text-muted)" }}>Verifying session…</span>
        </div>
      </>
    );
  }

  return (
    <div className="sp-shell">
      <StyleInjector />

      {/* ── Mobile backdrop ───────────────────────────────────────────── */}
      {navOpen && <div className="ad-backdrop" onClick={() => setNavOpen(false)} />}

      {/* ── Sidebar ───────────────────────────────────────────────────── */}
      <aside className={`ad-sidebar${navOpen ? " open" : ""}`}>
        <div className="ad-logo">
          <div className="ad-logo-icon"><HeadphonesIcon size={15} color="#fff" /></div>
          <div className="ad-logo-text"><strong>Tradexflow</strong><span>Admin Panel</span></div>
          <button className="ad-nav-close" onClick={() => setNavOpen(false)}><X size={16} /></button>
        </div>
        <nav className="ad-nav">
          {NAV_SECTIONS.map((section) => (
            <div key={section.label}>
              <div className="ad-nav-section-label">{section.label}</div>
              <div className="ad-nav-items">{section.items.map((item) => <SidebarLink key={item.to} {...item} onClick={() => setNavOpen(false)} />)}</div>
            </div>
          ))}
        </nav>
        <div className="ad-user-card">
          <div className="ad-user-inner">
            <div className="ad-user-avatar">{adminInitials.toUpperCase()}</div>
            <div className="ad-user-info"><strong>Administrator</strong><span>{adminEmail}</span></div>
            <button className="ad-logout-btn" onClick={handleLogout} title="Sign out"><LogOut size={13} /></button>
          </div>
        </div>
      </aside>

      {/* ── Page ──────────────────────────────────────────────────────── */}
      <div className="sp-page">

        {/* Topbar */}
        <header className="sp-topbar">
          <div className="sp-topbar-left">
            <button className="ad-hamburger" onClick={() => setNavOpen(true)}><Menu size={18} /></button>
            <div className="sp-topbar-badge"><HeadphonesIcon size={10} />Support</div>
            <div>
              <div className="sp-topbar-title">Support tickets</div>
              <div className="sp-topbar-sub">Manage user complaints and ticket conversations</div>
            </div>
          </div>
          <div className="sp-topbar-right">
            <button className="sp-btn" onClick={() => setEmailModal(true)} style={{ background: "var(--accent-dim)", color: "var(--accent-light)", borderColor: "rgba(99,102,241,0.3)" }}>
              <Mail size={12} /><span>Send Email</span>
            </button>
            <button className="sp-btn" onClick={fetchTickets}><RefreshCw size={12} /><span>Refresh</span></button>
          </div>
        </header>

        {/* Main */}
        <main className="sp-main">

          {/* Stats */}
          <div className="sp-stat-grid">
            {STAT_CARDS.map(({ label, value, icon: Icon, bg, color, glow, bar, sub }) => (
              <div key={label} className="sp-stat" style={{ "--stat-glow": glow, "--stat-bar": bar }}>
                <div className="sp-stat-top">
                  <span className="sp-stat-label">{label}</span>
                  <div className="sp-stat-icon" style={{ background: bg }}><Icon size={15} style={{ color }} /></div>
                </div>
                <div><div className="sp-stat-value">{value}</div>{sub && <div className="sp-stat-sub">{sub}</div>}</div>
              </div>
            ))}
          </div>

          {/* Table panel */}
          <div className="sp-panel">

            {/* Toolbar */}
            <div className="sp-toolbar">
              <div className="sp-toolbar-left">
                <span className="sp-panel-title">All tickets</span>
                <span className="sp-count-pill">{filtered.length}</span>
              </div>
              <div className="sp-toolbar-right">
                {/* Status filter */}
                <div className="sp-filter-group">
                  {STATUS_FILTERS.map((s) => (
                    <button
                      key={s}
                      className={`sp-filter-btn${statusFilter === s ? ` active ${s}` : ""}`}
                      onClick={() => { setStatusFilter(s); setPage(1); }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                {/* Search */}
                <div className="sp-search-wrap">
                  <Search size={12} className="sp-search-icon" />
                  <input
                    type="text" placeholder="Search tickets…"
                    value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    className="sp-search"
                  />
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="sp-table-wrap">
              <table className="sp-table">
                <thead>
                  <tr>
                    {COLS.map(({ key, label }) => (
                      <th key={key} onClick={() => handleSort(key)}>
                        {label}<SortIcon field={key} sortField={sortField} sortDir={sortDir} />
                      </th>
                    ))}
                    <th className="no-sort">Status</th>
                    <th className="no-sort" style={{ textAlign: "right" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 8 }).map((_, i) => (
                      <tr key={i}>{Array.from({ length: 7 }).map((_, j) => <td key={j}><div className="sp-skel" style={{ width: `${45 + (i * j * 11) % 40}%` }} /></td>)}</tr>
                    ))
                  ) : pageSlice.length === 0 ? (
                    <tr><td colSpan={7}><div className="sp-empty"><AlertCircle size={22} /><span>No tickets found.</span></div></td></tr>
                  ) : pageSlice.map((t) => {
                    const aColor = avatarColor(t.id);
                    return (
                      <tr key={t.id} onClick={() => setViewTicket(t)}>
                        {/* User */}
                        <td>
                          <div className="sp-avatar-cell">
                            <div className={`sp-avatar sp-avatar-${aColor}`}>{initials(t.users?.first_name, t.users?.last_name)}</div>
                            <div>
                              <div className="sp-cell-name">{t.users?.first_name} {t.users?.last_name}</div>
                              <div className="sp-cell-sub">{t.users?.email}</div>
                            </div>
                          </div>
                        </td>
                        {/* Subject */}
                        <td style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {t.subject
                            ? <span style={{ color: "var(--text-primary)", fontWeight: 500 }}>{t.subject}</span>
                            : <span style={{ color: "var(--text-muted)", fontStyle: "italic" }}>No subject</span>}
                        </td>
                        {/* Category */}
                        <td><span className="sp-category">{t.category ?? "—"}</span></td>
                        {/* Priority */}
                        <td><span className={`sp-priority ${t.priority ?? "medium"}`}>{t.priority ?? "—"}</span></td>
                        {/* Opened */}
                        <td style={{ whiteSpace: "nowrap", color: "var(--text-secondary)", fontSize: 12, fontFamily: "var(--font-mono)" }}>{fmtDateTime(t.created_at)}</td>
                        {/* Status */}
                        <td><StatusChip status={t.status} /></td>
                        {/* Action */}
                        <td>
                          <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <button className="sp-open-btn" onClick={(e) => { e.stopPropagation(); setViewTicket(t); }}>
                              <MessageSquare size={12} />Open
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="sp-pagination">
                <span className="sp-pag-info">
                  {filtered.length ? `${(page - 1) * PER_PAGE + 1}–${Math.min(page * PER_PAGE, filtered.length)} of ${filtered.length}` : "0 results"}
                </span>
                <div className="sp-pag-pages">
                  {Array.from({ length: Math.min(totalPages, 8) }, (_, i) => i + 1).map((p) => (
                    <button key={p} className={`sp-pag-btn${page === p ? " active" : ""}`} onClick={() => setPage(p)}>{p}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Ticket modal */}
      {viewTicket && (
        <TicketModal
          ticket={viewTicket}
          adminId={adminId}
          onClose={() => setViewTicket(null)}
          onStatusChange={handleStatusChange}
          showToast={showToast}
        />
      )}

      {/* Email modal */}
      {emailModal && (
        <SendEmailModal
          users={allUsers}
          adminEmail={adminEmail}
          onClose={() => setEmailModal(false)}
          showToast={showToast}
        />
      )}

      {/* Toast */}
      {toast.msg && (
        <div className={`sp-toast ${toast.ok ? "ok" : "err"}`}>
          {toast.ok
            ? <CheckCircle2 size={14} style={{ color: "var(--green)", flexShrink: 0 }} />
            : <XCircle      size={14} style={{ color: "var(--rose)",  flexShrink: 0 }} />}
          {toast.msg}
        </div>
      )}
    </div>
  );
}